import { LoanBookType } from '@/models/back-office-pages/loan-book'
import { FundingLine } from '@/models/back-office-pages/prospects'
import { AdministrationTypes, Currency, FromToFilterInput, MinMaxFilterInput } from '@/models/common'
import { Direction } from '@/models/table'

export enum AccountingNoteStatus {
  BLOCKED = 'BLOCKED',
  FAILED = 'FAILED',
  PAID = 'PAID',
  PROCESSING = 'PROCESSING',
  QUEUED = 'QUEUED',
}

export enum NoteType {
  CREDIT_NOTE = 'CREDIT_NOTE',
  PURCHASE_NOTE = 'PURCHASE_NOTE',
  VAT_NOTE = 'VAT_NOTE',
  RETURN_PAYMENT = 'RETURN_PAYMENT',
}

export interface NoteDTO {
  clientId: string
  companyName: string
  id: string
  issueDate: Date
  noteNumber: string
  status: AccountingNoteStatus
  totalAmountNotFactored: number
  totalAdvanceFactorFee: number
  totalReservedVat: number
  totalFactorFee: number
  totalFactorFeeVat: number
  totalInclVat: number
  totalAdvanceFactorFeeNonDiscounting: number
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  noteType: NoteType
  currency: Currency
}

export interface NoteTotalsDTO {
  subtotalFee: number
  totalAdvanceFactorFee: number
  totalAdvanceFactorFeeNonDiscounting: number
  totalAmountNotFactored: number
  totalFactorFee: number
  totalFactorFeeVat: number
  totalInclVat: number
  totalRefund: number
  totalReservedVat: number
  totalVat: number
}

export interface Page_NoteDTO {
  content: Array<NoteDTO>
  totals?: NoteTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface NotesData {
  accountingNoteList: Page_NoteDTO
}

export interface NotesVariables {
  sortDirection: Direction
  noteType: NoteType
  advanceTotalFeeInclVat?: MinMaxFilterInput
  companyName?: string
  sortField: string
  pageSize: number
  totalAdvancePayable?: MinMaxFilterInput
  issueDate?: FromToFilterInput
  pageNum: number
  noteNumber?: string
  currency?: Array<Currency>
}

export interface AccountingNoteFileDTO {
  content: string
  fileName: string
  size: number
}

export enum FileFormat {
  PDF = 'PDF',
  XLSX = 'XLSX',
  DOCX = 'DOCX',
}

export interface DownloadNoteData {
  downloadAccountingNote: AccountingNoteFileDTO
}

export interface DownloadNoteVariables {
  noteType: NoteType
  id: string
  fileFormat?: FileFormat
}

export interface DownloadNotesZipData {
  downloadAccountingNotesInZip: AccountingNoteFileDTO
}

export interface DownloadNotesZipVariables {
  accountingNoteIds: string[]
}

export interface DeleteNoteFromPayoutData {
  deleteNoteFromPayout: PayoutStatisticsDTO
}

export interface DeleteNoteFromPayoutVariables {
  payoutNoteId: string
}

export interface BlockNoteData {
  blockNote: boolean
}

export interface BlockNoteVariables {
  noteType: NoteType
  id: string
}

export interface UnBlockNoteData {
  unblockNote: boolean
}

export interface UnBlockNoteVariables {
  noteType: NoteType
  id: string
}

export interface MarkNoteAsPaidData {
  markNoteAsPaid: boolean
}

export interface MarkNoteAsPaidVariables {
  noteType: NoteType
  id: string
}

export interface DeleteNoteData {
  deleteNote: boolean
}

export interface DeleteNoteVariables {
  noteType: NoteType
  id: string
}

export interface UpdatePnFundedDateData {
  updatePurchaseNoteFundedDate: boolean
}

export interface UpdatePnFundedDateVariables {
  purchaseNoteId: string
  fundedDate: string
}

export interface UpdatePnFundedDateFormData {
  fundedDate: string | null
}

export enum UpdatePnFundedDateFieldNames {
  FUNDED_DATE = 'fundedDate',
}

export enum VatAdministrationTypes {
  BE_LOCAL = 'BE local',
  NL_LOCAL = 'NL local',
  LT_LOCAL = 'LT local',
  LT_SPV = 'LT SPV',
  LV_LOCAL = 'LV local',
  LV_SPV = 'LV SPV',
  PL_LOCAL = 'PL local',
}

export enum PayoutStatus {
  ARCHIVED = 'ARCHIVED',
  PAID = 'PAID',
  PREPARED = 'PREPARED',
  SENT_TO_BANK = 'SENT_TO_BANK',
}

export interface PayoutDTO {
  currency: Currency
  bankName: string
  fileName: string
  createdAt: Date
  createdBy: string
  id: string
  paymentMadeFromIban: string
  sourcingAgent: string
  sourcingAgentAdministrates: string
  status: PayoutStatus
  totalAmount: number
  totalTransactions: number
}

export interface Page_PayoutDTO {
  content: Array<PayoutDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface PayoutsData {
  payoutList: Page_PayoutDTO
}

export interface PayoutsVariables {
  createdAt?: FromToFilterInput
  sortDirection: Direction
  paymentMadeFromIban?: string
  productType?: PayoutProductType
  sortField: string
  pageSize: number
  pageNum: number
}

export interface DownloadPayoutData {
  downloadPayout: AccountingNoteFileDTO
}

export interface DownloadPayoutVariables {
  id: string
}

export interface PayoutStatisticsDTO {
  totalAmount: number
  totalTransactions: number
}

export interface GeneratePayoutData {
  generatePayout: PayoutStatisticsDTO
}

export enum PayoutProductType {
  FACTORING = 'FACTORING',
  LOAN_BOOK = 'LOAN_BOOK',
  INVESTMENTS = 'INVESTMENTS',
}

export enum InvestorPlanPaymentType {
  INTEREST_PAYMENT = 'INTEREST_PAYMENT',
  INVESTMENT = 'INVESTMENT',
  REPAYMENT = 'REPAYMENT',
  RETURN_PAYMENT = 'RETURN_PAYMENT',
  WITHDRAWAL = 'WITHDRAWAL',
  WITHDRAWAL_BEFORE_TERM = 'WITHDRAWAL_BEFORE_TERM',
}

export interface GeneratePayoutVariables {
  bankAccountId: string
  noteTypes: Array<NoteType>
  loanBookTypes: Array<LoanBookType>
  productType: PayoutProductType
  fundingLineTypes: Array<FundingLine>
  investorPlanPaymentTypes: Array<InvestorPlanPaymentType>
}

export enum GeneratePayoutFieldNames {
  ACCOUNT_ID = 'bankAccountId',
  LOAN_BOOK_TYPES = 'loanBookTypes',
  NOTE_TYPES = 'noteTypes',
  PRODUCT_TYPE = 'productType',
  FUNDING_LINE = 'fundingLineTypes',
  INVESTOR_PLAN_PAYMENT_TYPES = 'investorPlanPaymentTypes',
}

export interface PayoutNoteDTO {
  amount: number
  bankReference: string
  companyName: string
  createdAt: Date
  id: string
  noteId: string
  noteNumber: string
  noteType: NoteType
  productType: LoanBookType
  payoutId: string
  payoutStatus: PayoutStatus
  receiverIban: string
  updatedAt: Date
  currency: Currency
  reconciliationCreatedBy: string
  reconciliationCreatedByEmail: string
}

export interface Page_PayoutNoteDTO {
  content: Array<PayoutNoteDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface PayoutNotesData {
  payoutNoteList: Page_PayoutNoteDTO
}

export interface PayoutNotesVariables {
  sortDirection: Direction
  bankReference?: string
  companyName?: string
  sortField: string
  pageSize: number
  payoutId: string
  pageNum: number
  noteNumber?: string
  receiverIban?: string
}

export interface PayoutDetailsDTO {
  fileName: string
}

export interface PayoutDetailsData {
  payoutDetails: PayoutDetailsDTO
}

export interface PayoutDetailsVariables {
  payoutId: string
}

export interface ArchivePayoutData {
  archivePayout: PayoutDTO
}

export interface ArchivePayoutVariables {
  id: string
}

export interface MarkAsPaidPayoutData {
  markPayoutAsPaid: PayoutDTO
}

export interface MarkAsPaidPayoutVariables {
  id: string
}

export interface VatNoteClientDTO {
  clientId: string
  name: string
}

export interface VatNotesClientsData {
  loadVatNotesClients: Array<VatNoteClientDTO>
}

export interface VatNotesClientsVariables {
  sourcingAgentAdministrates: AdministrationTypes
  loanBookClients?: boolean
  period: string
}

export interface VatNoteGenerateResultDTO {
  generatedNotes: number
}

export interface GenerateVatNotesData {
  generateVatNotes: VatNoteGenerateResultDTO
}

export interface GenerateVatNotesVariables {
  period: string
  sourcingAgentAdministrates: AdministrationTypes | string
  clientIds: Array<string>
}

export enum GenerateVatNotesFieldsNames {
  MONTH_PERIOD = 'period',
  SA_ADMINISTRATES = 'sourcingAgentAdministrates',
  CLIENTS_IDS = 'clientIds',
}

export interface RegenerateVatNotesData {
  recreateVatNotes: VatNoteGenerateResultDTO
}

export interface RegenerateVatNotesVariables {
  vatNoteIds: Array<string>
}
